/**
 * Stock Status Report Component
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import api from '../../services/api';
import UniversalFilters from '../common/UniversalFilters';
import { ReportResponse, StockStatusItem, StockStatus } from '../../types/reports';
import { useFullscreen, FullscreenButton } from '../Common/FullscreenButton';

/* Stacked-chart palette — mirrors StockOutManagement chart styling */
const STATUS_COLORS: Record<string, string> = {
  stockout:       '#722F37', // deep wine — most severe
  critical:       '#B76E79', // rose
  medium_risk:    '#C9A96E', // amber
  healthy:        '#7BA672', // sage green
  overstock:      '#8B6F8B', // muted plum
  high_overstock: '#5D3A4A', // dark plum — sub-flag of overstock
};
const STATUS_LABELS: Record<string, string> = {
  stockout:       'Stockout',
  critical:       'Critical',
  medium_risk:    'Medium Risk',
  healthy:        'Healthy',
  overstock:      'Overstock',
  high_overstock: 'High Overstock',
};
const STATUS_ORDER = [
  'stockout',
  'critical',
  'medium_risk',
  'healthy',
  'overstock',
  'high_overstock',
];
const chartTooltipStyle = {
  background: '#FDFBF7',
  border: '1px solid #E8E0D4',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(114,47,55,0.08)',
  fontSize: '12px',
  padding: '10px 14px',
};

const StockStatusReport: React.FC = () => {
  const { isFullscreen, toggle: toggleFullscreen, FullscreenWrap } = useFullscreen();
  const [statusFilter, setStatusFilter] = useState('');

  // Universal filters state
  const [filters, setFilters] = useState<{
    brands: string[];
    stores: string[];
    categories: string[];
    cities: string[];
    abcClassifications: string[];
    start_date: string;
    end_date: string;
  }>({
    brands: [],
    stores: [],
    categories: [],
    cities: [],
    abcClassifications: [],
    start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  // Fetch report data
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery<ReportResponse<StockStatusItem>>({
    queryKey: ['stockStatusReport', filters, statusFilter],
    queryFn: async () => {
      const response = await api.getStockStatusReport({
        store_id: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
        category: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
        brand: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
        cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
        abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        status: statusFilter || undefined,
      });
      return response.data;
    },
  });

  // Fetch 12-month stock projection (forecasts + planned orders + transfers)
  const { data: projectionData } = useQuery<{ data: any[]; metadata: any }>({
    queryKey: ['stockProjection', filters],
    queryFn: async () => {
      const response = await api.getStockProjection({
        store_id: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
        category: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
        brand: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
        months: 12,
      });
      return response.data;
    },
  });

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const exportParams = {
        store_id: filters.stores.length > 0 ? filters.stores.join(',') : undefined,
        category: filters.categories.length > 0 ? filters.categories.join(',') : undefined,
        brand: filters.brands.length > 0 ? filters.brands.join(',') : undefined,
        cities: filters.cities.length > 0 ? filters.cities.join(',') : undefined,
        abc_classifications: filters.abcClassifications.length > 0 ? filters.abcClassifications.join(',') : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        status: statusFilter || undefined,
      };
      const response =
        format === 'csv'
          ? await api.exportReportCSV('stock_status', exportParams)
          : await api.exportReportJSON('stock_status', exportParams);

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock_status_${new Date().toISOString().split('T')[0]}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Export error - silently handled
    }
  };

  const getStatusColor = (
    status: string
  ): 'error' | 'warning' | 'success' | 'default' | 'info' => {
    switch (status) {
      case StockStatus.STOCKOUT:
        return 'error';
      case StockStatus.CRITICAL:
        return 'error';
      case StockStatus.MEDIUM_RISK:
        return 'warning';
      case StockStatus.HEALTHY:
        return 'success';
      case StockStatus.OVERSTOCK:
        return 'info';
      case StockStatus.HIGH_OVERSTOCK:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case StockStatus.STOCKOUT:       return 'STOCKOUT';
      case StockStatus.CRITICAL:       return 'CRITICAL';
      case StockStatus.MEDIUM_RISK:    return 'MEDIUM RISK';
      case StockStatus.HEALTHY:        return 'HEALTHY';
      case StockStatus.OVERSTOCK:      return 'OVERSTOCK';
      case StockStatus.HIGH_OVERSTOCK: return 'HIGH OVERSTOCK';
      default: return (status || '').toUpperCase();
    }
  };

  /* ----------------------------------------------------------------
   * Stacked chart data: SKU count per store, segmented by status.
   * Mirrors the StockOutManagement monthly chart (stacked bars).
   * ---------------------------------------------------------------- */
  const stackedChartData = useMemo(() => {
    const rows = reportData?.data || [];
    if (!rows.length) return [];

    const byStore = new Map<string, Record<string, any>>();
    rows.forEach((row: StockStatusItem) => {
      const store = row.store_name || 'Unknown';
      if (!byStore.has(store)) {
        const init: Record<string, any> = { store };
        STATUS_ORDER.forEach((s) => (init[s] = 0));
        byStore.set(store, init);
      }
      const bucket = byStore.get(store)!;
      const key = STATUS_ORDER.includes(row.status as string) ? (row.status as string) : 'healthy';
      bucket[key] = (bucket[key] || 0) + 1;
    });

    // Sort stores by total descending so the busiest sites lead the chart
    return Array.from(byStore.values())
      .map((b) => ({ ...b, total: STATUS_ORDER.reduce((sum, s) => sum + (b[s] || 0), 0) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 25); // cap to keep chart readable
  }, [reportData]);

  return (
    <FullscreenWrap>
    <Box>
      {/* Universal Filters */}
      <UniversalFilters
        brands={filters.brands}
        stores={filters.stores}
        categories={filters.categories}
        cities={filters.cities}
        abcClassifications={filters.abcClassifications}
        onBrandsChange={(brands) => setFilters({ ...filters, brands })}
        onStoresChange={(stores) => setFilters({ ...filters, stores })}
        onCategoriesChange={(categories) => setFilters({ ...filters, categories })}
        onCitiesChange={(cities) => setFilters({ ...filters, cities })}
        onAbcClassificationsChange={(abcClassifications) => setFilters({ ...filters, abcClassifications })}
        onClearAll={() => { setFilters({ brands: [], stores: [], categories: [], cities: [], abcClassifications: [], start_date: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], end_date: new Date().toISOString().split('T')[0] }); setStatusFilter(''); }}
        showCountries={false}
        showRetailers={false}
        showBrands={true}
        showStores={true}
        showCategories={true}
        showCities={true}
        showAbcClassifications={true}
        customFilters={
          <>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small" type="date" label="Start Date" fullWidth
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                size="small" type="date" label="End Date" fullWidth
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Stock Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Stock Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="stockout">Stockout</MenuItem>
                  <MenuItem value="critical">Critical (&lt; 1 week)</MenuItem>
                  <MenuItem value="medium_risk">Medium Risk (&lt; 75% target)</MenuItem>
                  <MenuItem value="healthy">Healthy (75–175%)</MenuItem>
                  <MenuItem value="overstock">Overstock (&gt; 175%)</MenuItem>
                  <MenuItem value="high_overstock">High Overstock (&gt; 6 mo)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('csv')}
                >
                  Export
                </Button>
                <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
              </Box>
            </Grid>
          </>
        }
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading report: {(error as Error).message}
        </Alert>
      )}

      {reportData && (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Generated: {new Date(reportData.metadata.generated_at).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Items: {reportData.metadata.record_count}
            </Typography>
          </Box>

          {/* 12-month stock projection: forecasts × planned orders × transfers */}
          {projectionData?.data && projectionData.data.length > 0 && (
            <Box
              sx={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                p: 3,
                mb: 3,
                background: 'var(--color-bg-elevated, #fff)',
              }}
            >
              <Typography
                sx={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-heading)',
                  mb: 0.5,
                }}
              >
                12-Month Stock Projection
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'block', mb: 2 }}>
                Forward-looking SKU status projection — combines current stock, demand forecasts, planned replenishment orders and in-flight stock transfers.
              </Typography>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart
                  data={projectionData.data}
                  margin={{ top: 5, right: 20, left: 10, bottom: 30 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                  <XAxis
                    dataKey="month_label"
                    tick={{ fontSize: 11, fill: '#8A8A85' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E5E5E0' }}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#8A8A85' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    formatter={(value: any, name: string) => [value, STATUS_LABELS[name] || name]}
                    contentStyle={chartTooltipStyle}
                    labelFormatter={(label: string) => `Month: ${label}`}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '11px' }}
                    formatter={(val: string) => STATUS_LABELS[val] || val}
                  />
                  {STATUS_ORDER.map((status, idx) => (
                    <Bar
                      key={status}
                      dataKey={status}
                      stackId="projection"
                      name={status}
                      fill={STATUS_COLORS[status]}
                      radius={idx === STATUS_ORDER.length - 1 ? [3, 3, 0, 0] : undefined}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* Stacked stock-status distribution chart (per store) */}
          {stackedChartData.length > 0 && (
            <Box
              sx={{
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                p: 3,
                mb: 3,
                background: 'var(--color-bg-elevated, #fff)',
              }}
            >
              <Typography
                sx={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-heading)',
                  mb: 2,
                }}
              >
                Stock Status Distribution by Store
              </Typography>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart
                  data={stackedChartData}
                  margin={{ top: 5, right: 20, left: 10, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEA" vertical={false} />
                  <XAxis
                    dataKey="store"
                    tick={{ fontSize: 10, fill: '#8A8A85' }}
                    tickLine={false}
                    axisLine={{ stroke: '#E5E5E0' }}
                    interval={0}
                    angle={-30}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#8A8A85' }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    formatter={(value: any, name: string) => [value, STATUS_LABELS[name] || name]}
                    contentStyle={chartTooltipStyle}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '11px' }}
                    formatter={(val: string) => STATUS_LABELS[val] || val}
                  />
                  {STATUS_ORDER.map((status, idx) => (
                    <Bar
                      key={status}
                      dataKey={status}
                      stackId="stockstatus"
                      name={status}
                      fill={STATUS_COLORS[status]}
                      radius={idx === STATUS_ORDER.length - 1 ? [3, 3, 0, 0] : undefined}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}

          <TableContainer
            component={Paper}
            sx={{
              width: '100%',
              maxWidth: '100%',
              overflowX: 'auto',
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 280px)',
              // Always show the horizontal scrollbar so users discover it
              '&::-webkit-scrollbar': { height: 12, width: 12 },
              '&::-webkit-scrollbar-track': { background: '#F5F1E8' },
              '&::-webkit-scrollbar-thumb': {
                background: '#C9A96E',
                borderRadius: 6,
                border: '2px solid #F5F1E8',
              },
              '&::-webkit-scrollbar-thumb:hover': { background: '#B76E79' },
              scrollbarColor: '#C9A96E #F5F1E8',
              scrollbarWidth: 'thin',
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 1700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Stock Status</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Lifecycle</TableCell>
                  <TableCell>Item Status</TableCell>
                  <TableCell align="right">Current Stock</TableCell>
                  <TableCell align="right">In Transit</TableCell>
                  <TableCell align="right">Target Stock</TableCell>
                  <TableCell align="right">Avg Daily Sales</TableCell>
                  <TableCell align="right">Days of Supply</TableCell>
                  <TableCell align="right">Months of Cover</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Suggested Action Plan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} align="center">
                      No data available with current filters
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.data.map((item: StockStatusItem, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(item.status)}
                          color={getStatusColor(item.status)}
                          size="small"
                          variant={item.status === StockStatus.HIGH_OVERSTOCK ? 'filled' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>{item.store_name}</TableCell>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.brand || '—'}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell sx={{ py: '6px', px: '8px' }}>
                        {item.lifecycle_stage ? (
                          <Chip
                            label={item.lifecycle_stage}
                            size="small"
                            color={
                              item.lifecycle_stage === 'NEW' ? 'info' :
                              item.lifecycle_stage === 'GROWTH' ? 'success' :
                              item.lifecycle_stage === 'DECLINING' ? 'warning' :
                              (item.lifecycle_stage === 'PHASE_OUT' || item.lifecycle_stage === 'DISCONTINUED') ? 'error' :
                              'default'
                            }
                            variant="outlined"
                            sx={{ fontSize: '10px', height: 20 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`status-badge ${
                          item.item_status === 'RED' ? 'status-badge--danger' :
                          item.item_status === 'YELLOW' ? 'status-badge--warning' : 'status-badge--success'
                        }`}>
                          {item.item_status || '—'}
                        </span>
                      </TableCell>
                      <TableCell align="right">{item.current_stock}</TableCell>
                      <TableCell align="right">{item.in_transit}</TableCell>
                      <TableCell align="right">
                        {Math.round(Number(item.target_stock) || 0).toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        {Math.round(Number(item.avg_daily_sales) || 0)}
                      </TableCell>
                      <TableCell align="right">
                        <strong>{item.days_of_supply.toFixed(1)}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {(item.months_of_cover ?? 0).toFixed(1)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography variant="caption" sx={{ fontSize: '12px' }}>
                          {item.suggested_action || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
    </FullscreenWrap>
  );
};

export default StockStatusReport;
